<template>
  <v-data-table
    :headers="computedTableHeaders"
    :items="categoryItems"
    :disable-sort="disableSort"
    :expanded.sync="expanded"
    :disable-pagination="disablePagination"
    :disable-filtering="disableFiltering"
    :hide-default-footer="hideDefaultFooter"
    :item-key="categoryItemKey"
    @click:row="rowClick"
    show-expand
    show-select
  >
    <template v-slot:top>
      <v-row
        dense
        align="center"
        justify="start"
      >
        <v-switch
          v-if="isUseExtendData"
          v-model="isShowAdditionalData"
          :label="extendDataLabel"
          class="pa-3"
        ></v-switch>
        <XlsxExport
          v-if="isUseXlsxExport && !hasSomeAbilities($constants.ABILITY.INTERN)"
          :filename="xlsxFilename"
          :sheets="computedExportSheet"
          tooltip="export to xlsx"
        />
      </v-row>
    </template>

    <template v-slot:header.data-table-select="{ }">
      <v-simple-checkbox
        :ripple="false"
        :value="isAllCategoriesSelected()"
        @input="selectAllCategoriesToggle($event)"
      ></v-simple-checkbox>
    </template>

    <template v-slot:item.data-table-select="{ item }">
      <v-simple-checkbox
        v-if="item[categoryItemKey] !== 'total'"
        :ripple="false"
        :value="checkIfCategoryIsSelected(item[categoryItemKey])"
        @input="selectAllCategoryDataItemsToggle($event, item[categoryItemKey])"
      ></v-simple-checkbox>
    </template>

    <template v-for="header in categoryHeaders"
              v-slot:[`item.${header.value}`]="{ item }"
    >
      {{ (header.calc !== undefined && typeof header.calc === 'function' )
          ?  header.calc(item)
          : item[header.value]
      }}
    </template>

    <template v-slot:expanded-item="{ headers, item }">
      <td :colspan="headers.length">
          <v-data-table
            :headers="computedExpandedTableHeaders"
            :items="getDataItemsForCategory(item[dataItemCategoryKey])"
            :disable-sort="true"
            :disable-filtering="true"
            show-select
          >

            <template v-slot:header.data-table-select="{ }">
              <v-simple-checkbox
                :ripple="false"
                :value="checkIfAllCategoryDataItemsAreSelected(item[dataItemCategoryKey])"
                @input="selectAllCategoryDataItemsToggle($event, item[dataItemCategoryKey])"
              ></v-simple-checkbox>
            </template>

            <template v-slot:item.data-table-select="{ item }">
              <v-simple-checkbox
                :ripple="false"
                :value="item.is_selected"
                @input="categoryDataItemSelectToggle($event, item)"
              ></v-simple-checkbox>
            </template>

            <template v-for="header in expandedTableHeaders"
                      v-slot:[`item.${header.value}`]="{ item }"
            >
              {{ (header.calc !== undefined && typeof header.calc === 'function' )
              ?  header.calc(item)
              : item[header.value]
              }}
            </template>
          </v-data-table>
        </td>
    </template>
  </v-data-table>
</template>

<script>
import XlsxExport from '../XlsxExport.vue';

export default {
  name: 'ExpandableTable',
  components: { XlsxExport },

  props: {
    categoryItems: {
      type: Array,
      default: () => [],
    },

    categoryHeaders: {
      type: Array,
      default: () => [],
    },

    categoryItemKey: {
      type: String,
      default: 'rating_option',
    },

    dataItems: {
      type: Array,
      default: () => [],
    },

    extractCategoryDataItems: {
      type: Function,
      default: null,
    },

    dataItemCategoryKey: {
      type: String,
      default: 'rating_option',
    },

    expandedTableHeaders: {
      type: Array,
    },

    isUseExtendData: {
      type: Boolean,
      default: true,
    },

    extendDataLabel: {
      type: String,
      default: 'Additional view',
    },

    isUseXlsxExport: {
      type: Boolean,
      default: true,
    },

    xlsxFilename: {
      type: String,
      default: 'export.xlsx',
    },

    disableSort: {
      type: Boolean,
      default: true,
    },

    disablePagination: {
      type: Boolean,
      default: true,
    },

    disableFiltering: {
      type: Boolean,
      default: true,
    },

    hideDefaultFooter: {
      type: Boolean,
      default: true,
    },

    extraXlsxPages: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isShowAdditionalData: false,
      expanded: [],
      categoriesSelected: [],
    };
  },

  methods: {
    rowClick(item, event) {
      event.expand(!event.isExpanded);
    },

    categoryDataItemSelectToggle(value, selectedItem) {
      const dataItemIndex = this.dataItems
        .findIndex((el) => el.id === selectedItem.id);

      this.dataItems[dataItemIndex].is_selected = value;
    },

    checkIfAllCategoryDataItemsAreSelected(categoryName) {
      const selectableDataItems = this.getDataItemsForCategory(categoryName);

      return selectableDataItems.length === selectableDataItems
        .filter((el) => el.is_selected).length;
    },

    selectAllCategoryDataItemsToggle(value, ratingOption) {
      for (let i = 0; i < this.dataItems.length; i += 1) {
        if (this.dataItems[i].rating_list.includes(ratingOption)) {
          this.dataItems[i].is_selected = value;
        }
      }
    },

    checkIfCategoryIsSelected(categoryName) {
      const isCategorySelected = this.checkIfAllCategoryDataItemsAreSelected(categoryName);

      const categoryWasAlreadySelected = this.categoriesSelected.includes(categoryName);

      if (isCategorySelected && !categoryWasAlreadySelected) {
        this.categoriesSelected.push(categoryName);
      }
      if (!isCategorySelected && categoryWasAlreadySelected) {
        this.categoriesSelected = this.categoriesSelected.filter((el) => el !== categoryName);
      }

      return categoryWasAlreadySelected;
    },

    isAllCategoriesSelected() {
      return this.categoriesSelected.length === this.categoryItems.length - 1;
    },

    selectAllCategoriesToggle(value) {
      for (let i = 0; i < this.dataItems.length; i += 1) {
        this.dataItems[i].is_selected = value;
      }
    },

    getDataItemsForCategory(categoryName) {
      if (this.extractCategoryDataItems !== null && typeof this.extractCategoryDataItems === 'function') {
        return this.extractCategoryDataItems(categoryName);
      }

      return categoryName === 'total'
        ? this.dataItems
        : this.dataItems
          .filter((el) => el.rating_list.includes(categoryName));
    },
  },

  computed: {
    computedTableHeaders() {
      // eslint-disable-next-line max-len
      return this.categoryHeaders.filter((el) => el.is_export_only === undefined || !el.is_export_only);
    },

    computedExportHeaders() {
      // eslint-disable-next-line max-len
      return this.categoryHeaders.filter((el) => el.is_page_only === undefined || !el.is_page_only);
    },

    computedExpandedTableHeaders() {
      // eslint-disable-next-line max-len
      return this.computedExpandedExportHeaders.filter((el) => el.is_export_only === undefined || !el.is_export_only);
    },

    computedExpandedExportHeaders() {
      return this.isShowAdditionalData
        ? this.expandedTableHeaders
        : this.expandedTableHeaders.filter((el) => !el.isAdditional);
    },

    computedExportSheet() {
      // eslint-disable-next-line max-len
      const sheets = !this.categoryItems.length || !this.dataItems.length
        ? [
          {
            name: 'Categories',
            data: [{}],
          },
        ]
        : [
          {
            name: 'Categories',
            data: this.categoryItems.map((el) => {
              const item = {};
              // eslint-disable-next-line no-restricted-syntax
              for (const headerItem of this.computedExportHeaders) {
                item[headerItem.text] = headerItem.calc(el);
              }

              return item;
            }),
          },
          ...this.categoryItems.map((el) => ({
            name: this.computedExportHeaders.find((h) => h.value === 'category').calc(el) || 'Category',
            data: !this.getDataItemsForCategory(el[this.categoryItemKey]).length
              ? [{}]
              : this.getDataItemsForCategory(el[this.categoryItemKey]).map((route) => {
                const item = {};
                // eslint-disable-next-line no-restricted-syntax
                for (const headerItem of this.computedExpandedExportHeaders) {
                  item[headerItem.text] = headerItem.calc(route);
                }

                return item;
              }),
          })),
        ];

      if (this.categoryItems.length && this.dataItems.length && this.extraXlsxPages.length) {
        sheets.push(...this.extraXlsxPages);
      }

      return sheets;
    },
  },
};
</script>

<style scoped>

</style>
